import { useEffect } from 'react';

/**
 * Ports the original funmiyo-website.html script: section tabs,
 * accordion, video carousel, YouTube embeds, keyboard nav.
 */
export default function useFunmiyoBook(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    let cur = 0;
    const secs = [...root.querySelectorAll('.sec')];

    function show(i) {
      if (i < 0 || i >= secs.length) return;
      cur = i;
      secs.forEach((s, k) => s.classList.toggle('on', k === i));
      root.querySelectorAll('.dtab').forEach((t) =>
        t.classList.toggle('on', +t.dataset.div === i),
      );
      root.querySelectorAll('.mtab').forEach((t) =>
        t.classList.toggle('on', +t.dataset.div === i),
      );
      const strip = root.querySelector('#strip');
      if (strip) strip.style.display = i === 0 ? 'none' : 'flex';
      secs[i].scrollTop = 0;
    }

    const tabHandlers = [...root.querySelectorAll('.dtab,.mtab')].map((t) => {
      const fn = () => show(+t.dataset.div);
      t.addEventListener('click', fn);
      return [t, fn];
    });

    const goHandlers = [...root.querySelectorAll('[data-go]')].map((b) => {
      const fn = () => show(+b.dataset.go);
      b.addEventListener('click', fn);
      return [b, fn];
    });

    const stripmark = root.querySelector('#stripmark');
    const onStrip = () => show(0);
    if (stripmark) stripmark.addEventListener('click', onStrip);

    const onKey = (e) => {
      if (e.target.closest('input,textarea')) return;
      if (e.key === 'ArrowRight') show(cur + 1);
      if (e.key === 'ArrowLeft') show(cur - 1);
    };
    document.addEventListener('keydown', onKey);

    const vheadHandlers = [...root.querySelectorAll('.vhead')].map((h) => {
      const fn = () => {
        const it = h.parentElement;
        const was = it.classList.contains('on');
        it.closest('.visits').querySelectorAll('.visit').forEach((v) =>
          v.classList.remove('on'),
        );
        if (!was) it.classList.add('on');
      };
      h.addEventListener('click', fn);
      return [h, fn];
    });

    const fqHandlers = [...root.querySelectorAll('.fq')].map((q) => {
      const fn = () => {
        const it = q.parentElement;
        const was = it.classList.contains('on');
        it.closest('.faq').querySelectorAll('.fitem').forEach((f) =>
          f.classList.remove('on'),
        );
        if (!was) it.classList.add('on');
      };
      q.addEventListener('click', fn);
      return [q, fn];
    });

    const track = root.querySelector('#vtrack');
    let onResize = null;
    let vprev = null;
    let vnext = null;
    let onPrev = null;
    let onNext = null;
    const thumbCleanups = [];

    if (track) {
      const cards = [...track.querySelectorAll('.vcard')];
      const dots = root.querySelector('#vdots');
      let vi = 0;
      const per = () =>
        window.innerWidth < 760 ? 1 : window.innerWidth < 1100 ? 2 : 3;

      function layout() {
        const pv = per();
        const gap = 14;
        const w = (track.parentElement.offsetWidth - (pv - 1) * gap) / pv;
        cards.forEach((c) => {
          c.style.width = w + 'px';
        });
        const max = Math.max(0, cards.length - pv);
        vi = Math.min(vi, max);
        track.style.transform = `translateX(-${vi * (w + gap)}px)`;
        if (dots) {
          dots.innerHTML = '';
          for (let k = 0; k <= max; k++) {
            const d = document.createElement('div');
            d.className = 'vdot' + (k === vi ? ' on' : '');
            d.onclick = () => {
              vi = k;
              layout();
            };
            dots.appendChild(d);
          }
        }
        const prevBtn = root.querySelector('#vprev');
        const nextBtn = root.querySelector('#vnext');
        if (prevBtn) prevBtn.disabled = vi === 0;
        if (nextBtn) nextBtn.disabled = vi >= max;
      }

      vprev = root.querySelector('#vprev');
      vnext = root.querySelector('#vnext');
      onPrev = () => {
        vi = Math.max(0, vi - 1);
        layout();
      };
      onNext = () => {
        vi = vi + 1;
        layout();
      };
      if (vprev) vprev.addEventListener('click', onPrev);
      if (vnext) vnext.addEventListener('click', onNext);
      onResize = layout;
      window.addEventListener('resize', onResize);

      cards.forEach((c) => {
        const th = c.querySelector('.vthumb');
        if (!th) return;
        const play = () => {
          if (th.querySelector('iframe')) return;
          th.innerHTML =
            '<iframe src="https://www.youtube-nocookie.com/embed/' +
            c.dataset.vid +
            '?autoplay=1&rel=0" title="Funmath testimonial" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" allowfullscreen></iframe>';
        };
        const onKeyThumb = (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            play();
          }
        };
        th.addEventListener('click', play);
        th.addEventListener('keydown', onKeyThumb);
        thumbCleanups.push(() => {
          th.removeEventListener('click', play);
          th.removeEventListener('keydown', onKeyThumb);
        });
      });

      layout();
    }

    const bwHandlers = [...root.querySelectorAll('.bw')].map((w) => {
      const fn = () => {
        w.querySelectorAll('span').forEach((s) => {
          s.style.animation = 'none';
          void s.offsetHeight;
          s.style.animation = '';
        });
      };
      w.addEventListener('mouseleave', fn);
      return [w, fn];
    });

    show(0);

    return () => {
      tabHandlers.forEach(([el, fn]) => el.removeEventListener('click', fn));
      goHandlers.forEach(([el, fn]) => el.removeEventListener('click', fn));
      if (stripmark) stripmark.removeEventListener('click', onStrip);
      document.removeEventListener('keydown', onKey);
      vheadHandlers.forEach(([el, fn]) => el.removeEventListener('click', fn));
      fqHandlers.forEach(([el, fn]) => el.removeEventListener('click', fn));
      if (vprev && onPrev) vprev.removeEventListener('click', onPrev);
      if (vnext && onNext) vnext.removeEventListener('click', onNext);
      if (onResize) window.removeEventListener('resize', onResize);
      thumbCleanups.forEach((fn) => fn());
      bwHandlers.forEach(([el, fn]) => el.removeEventListener('mouseleave', fn));
    };
  }, [rootRef]);
}
